import type { LauraAudit } from "@/app/components/shared/types";
import { LauraStageShell } from "../shared/LauraStageShell";
import { defaultP6FormData, type P6FormData } from "./p6Types";
import { useTranslation } from "@/app/i18n/LocaleContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Field, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

const PHASE_PROGRESS: Record<P6FormData["milestones"][0]["phase"], number> = {
    planning: 25,
    execution: 60,
    reporting: 90,
};

type Props = {
    audit: LauraAudit;
    deliverableContent: string;
    onDeliverableSaved: (content: string) => void;
    onStageStateChange?: (state: import("@/app/lib/auditflowApi").LauraStageState | null) => void;
};

export function LauraP6Workspace(props: Props) {
    const { t } = useTranslation();
    return (
        <LauraStageShell<P6FormData>
            audit={props.audit}
            stageId="p6"
            deliverableContent={props.deliverableContent}
            onDeliverableSaved={props.onDeliverableSaved}
            onStageStateChange={props.onStageStateChange}
            defaultForm={defaultP6FormData}
            title={t("laura", "p6Title")}
            description="Consolidated audit plan — summary, timeline, team, and resources"
            generateLabel={t("laura", "generatePlan")}
        >
            {({ form, setForm }) => (
                <div className="flex flex-col gap-4">
                    <Badge
                        variant={
                            form.document_status === "approved" ? "default" : "secondary"
                        }
                    >
                        {form.document_status.toUpperCase()}
                    </Badge>

                    <Card className="border-l-4 border-l-primary">
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm">
                                {t("laura", "executiveSummary")}
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <Textarea
                                rows={6}
                                value={form.executive_summary}
                                onChange={(e) =>
                                    setForm((p) => ({
                                        ...p,
                                        executive_summary: e.target.value,
                                    }))
                                }
                            />
                        </CardContent>
                    </Card>

                    <GanttTimeline milestones={form.milestones} />

                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                            const n = form.milestones.length + 1;
                            setForm((p) => ({
                                ...p,
                                milestones: [
                                    ...p.milestones,
                                    {
                                        code: `M-${String(n).padStart(2, "0")}`,
                                        activity: "",
                                        responsible: "",
                                        start_date: "",
                                        end_date: "",
                                        status: "planned",
                                        phase: "planning",
                                    },
                                ],
                            }));
                        }}
                    >
                        + milestone
                    </Button>

                    <Card>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm">Resources</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <FieldGroup>
                                <Field>
                                    <FieldLabel>Auditor hours</FieldLabel>
                                    <Input
                                        value={form.resources.auditor_hours}
                                        onChange={(e) =>
                                            setForm((p) => ({
                                                ...p,
                                                resources: {
                                                    ...p.resources,
                                                    auditor_hours: e.target.value,
                                                },
                                            }))
                                        }
                                    />
                                </Field>
                                <Field>
                                    <FieldLabel>Supervisor hours</FieldLabel>
                                    <Input
                                        value={form.resources.supervisor_hours}
                                        onChange={(e) =>
                                            setForm((p) => ({
                                                ...p,
                                                resources: {
                                                    ...p.resources,
                                                    supervisor_hours: e.target.value,
                                                },
                                            }))
                                        }
                                    />
                                </Field>
                                <Field>
                                    <FieldLabel>Travel notes</FieldLabel>
                                    <Textarea
                                        rows={2}
                                        value={form.resources.travel_notes}
                                        onChange={(e) =>
                                            setForm((p) => ({
                                                ...p,
                                                resources: {
                                                    ...p.resources,
                                                    travel_notes: e.target.value,
                                                },
                                            }))
                                        }
                                    />
                                </Field>
                            </FieldGroup>
                        </CardContent>
                    </Card>

                    {form.planning_risks.length > 0 && (
                        <Card>
                            <CardHeader className="pb-2">
                                <CardTitle className="text-sm">Planning risks</CardTitle>
                            </CardHeader>
                            <CardContent className="flex flex-col gap-2">
                                {form.planning_risks.map((r, i) => (
                                    <div
                                        key={i}
                                        className="rounded-md border p-2 text-xs"
                                    >
                                        <p className="font-medium">{r.risk}</p>
                                        <p className="text-muted-foreground">
                                            P: {r.probability} · Mitigation: {r.mitigation}
                                        </p>
                                    </div>
                                ))}
                            </CardContent>
                        </Card>
                    )}
                </div>
            )}
        </LauraStageShell>
    );
}

function GanttTimeline({
    milestones,
}: {
    milestones: P6FormData["milestones"];
}) {
    if (milestones.length === 0) return null;
    return (
        <Card>
            <CardHeader className="pb-2">
                <CardTitle className="text-sm">Timeline</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-3">
                {milestones.map((m) => (
                    <div key={m.code} className="flex flex-col gap-1">
                        <div className="flex items-center gap-2 text-xs">
                            <span className="w-14 font-mono">{m.code}</span>
                            <span className="flex-1 truncate">{m.activity}</span>
                            <Badge variant="outline">{m.phase}</Badge>
                        </div>
                        <Progress
                            value={PHASE_PROGRESS[m.phase]}
                            className={cn(
                                m.phase === "planning" && "[&>div]:bg-primary",
                                m.phase === "execution" && "[&>div]:bg-secondary",
                                m.phase === "reporting" && "[&>div]:bg-muted-foreground",
                            )}
                        />
                        <span className="text-[10px] text-muted-foreground">
                            {m.start_date} – {m.end_date} · {m.responsible}
                        </span>
                    </div>
                ))}
            </CardContent>
        </Card>
    );
}
